import React, { useCallback, useState } from 'react';
import { useParams } from 'react-router-dom';
import { useDispatch } from 'react-redux';
import { magicLinkActions } from '../../_actions/magicLink.actions';

/**
 * /magic/:token — landing page for magic-link clicks.
 *
 * Consumes the token via POST /v2/magic-link/consume, stores the returned
 * access/refresh tokens in localStorage, and routes the user to either
 * password setup (claim flow) or their dashboard (login flow).
 *
 * The link is used only when the person taps Continue, never on page load:
 * mail scanners (Outlook Safe Links, Defender, Mimecast) open links before
 * the recipient does, and a load-time consume burned the one-time token
 * (staging 2026-10-23: 1 invite in 3 arrived "already used").
 *
 * Spec: docs/client-flows.md §5.6
 */
export default function MagicLinkLanding() {
    const { token } = useParams();
    const dispatch = useDispatch();
    const [status, setStatus] = useState(token ? 'ready' : 'error'); // ready | verifying | success | error
    const [errorMsg, setErrorMsg] = useState(token ? '' : 'Missing token');

    const consume = useCallback(() => {
        if (!token) return;
        setStatus('verifying');

        dispatch(magicLinkActions.consumeToken(token))
            .then((response) => {
                // The API reports failures as HTTP 200 + {statusCode:0, error}
                // — surface the real reason (used/expired link etc).
                if (response?.data?.statusCode === 0) {
                    setStatus('error');
                    setErrorMsg(response?.data?.error?.responseMessage || 'Link is invalid or expired');
                    return;
                }
                const data = response?.data?.responseData || response?.data?.data || {};

                // Guest "view or cancel your booking" link (GUEST-BOOKING-PLAN
                // §4.3): a BOOKING-scoped token, not a session. Don't touch
                // localStorage (the browser may hold someone else's session,
                // and a guest must never be logged in as anyone). Land on the
                // confirmation page, which reads the manage token from the
                // query and authenticates the booking fetch with it.
                if (data.purpose === 'manage_booking' && data.bookingId) {
                    setStatus('success');
                    const salonRef = data.salonSlug || data.salonId;
                    const base = data.salonSlug ? '/book' : '/salon';
                    const path = data.hairdresserId
                        ? `${base}/${salonRef}/professional/${data.hairdresserId}/success`
                        : `${base}/${salonRef}/success`;
                    const manageToken = data.manageToken || token;
                    setTimeout(() => {
                        window.location.replace(`${path}?bid=${encodeURIComponent(data.bookingId)}&guest=1&token=${encodeURIComponent(manageToken)}`);
                    }, 400);
                    return;
                }

                if (!data.accessToken) {
                    setStatus('error');
                    setErrorMsg('Link could not be verified');
                    return;
                }

                // IDENTITY SWITCH. This browser may hold a previous session —
                // salon staff testing, another client, a stale subscription
                // gate. Wipe it and rebuild from the magic-link identity so
                // nothing from the old session leaks into the new one.
                localStorage.clear();
                localStorage.setItem('accessToken', data.accessToken);
                if (data.refreshToken) localStorage.setItem('refreshToken', data.refreshToken);
                if (data.userId) localStorage.setItem('userId', String(data.userId));
                if (data.defaultRedirectTo !== undefined && data.defaultRedirectTo !== null) {
                    localStorage.setItem('defaultRedirectTo', String(data.defaultRedirectTo));
                }

                setStatus('success');

                // FULL page load (not SPA navigation): routes.js must re-boot
                // so the profile + gate fetches run as the NEW user — an SPA
                // push keeps redux populated with the old identity and the
                // client dashboard bails to the marketplace.
                // Both purposes land on the CLIENT side, on their account
                // page — profile details + (eventually) password setup live
                // there. (First-password setup for claim accounts is a spec
                // §5 follow-up: change-password still expects an old password.)
                setTimeout(() => {
                    // Claim links: a password already exists → straight to
                    // their bookings; otherwise set-password first, which then
                    // continues to bookings. Login links keep landing on the
                    // account page.
                    if (data.purpose === 'claim') {
                        const needsSetup = data.needsPasswordSetup !== false;
                        window.location.replace(needsSetup ? '/dashboard/account?setup=password' : '/dashboard/bookings');
                    } else {
                        window.location.replace(data.needsPasswordSetup ? '/dashboard/account?setup=password' : '/dashboard/account');
                    }
                }, 600);
            })
            .catch((err) => {
                setStatus('error');
                const msg = err?.response?.data?.message || err?.message || 'Link is invalid or expired';
                setErrorMsg(msg);
            });
    }, [token, dispatch]);

    return (
        <div style={styles.wrapper}>
            <div style={styles.card}>
                {status === 'ready' && (
                    <>
                        <h2 style={styles.title}>Welcome to Beu</h2>
                        <p style={styles.subtitle}>Tap below to carry on.</p>
                        <button type="button" onClick={consume} style={styles.button}>Continue</button>
                    </>
                )}
                {status === 'verifying' && (
                    <>
                        <div style={styles.spinner} />
                        <h2 style={styles.title}>Signing you in…</h2>
                        <p style={styles.subtitle}>One moment while we verify your link.</p>
                    </>
                )}
                {status === 'success' && (
                    <>
                        <div style={styles.checkmark}>✓</div>
                        <h2 style={styles.title}>You're in</h2>
                        <p style={styles.subtitle}>Redirecting…</p>
                    </>
                )}
                {status === 'error' && (
                    <>
                        <div style={styles.cross}>!</div>
                        <h2 style={styles.title}>Link couldn't be used</h2>
                        <p style={styles.subtitle}>{errorMsg}</p>
                        <p style={styles.helper}>
                            <a href="/login" style={styles.link}>Sign in</a> to carry on.
                        </p>
                    </>
                )}
            </div>
        </div>
    );
}

const styles = {
    wrapper: {
        minHeight: '100vh',
        display: 'flex',
        alignItems: 'center',
        justifyContent: 'center',
        padding: '24px',
        background: '#f7f8fa'
    },
    card: {
        background: '#fff',
        borderRadius: '16px',
        boxShadow: '0 8px 24px rgba(0,0,0,0.08)',
        padding: '40px 32px',
        textAlign: 'center',
        maxWidth: '420px',
        width: '100%'
    },
    spinner: {
        width: '44px',
        height: '44px',
        border: '4px solid #e8e8e8',
        borderTopColor: '#007EA0',
        borderRadius: '50%',
        margin: '0 auto 24px',
        animation: 'spin 0.8s linear infinite'
    },
    checkmark: {
        width: '56px',
        height: '56px',
        margin: '0 auto 24px',
        borderRadius: '50%',
        background: '#0f5132',
        color: '#fff',
        fontSize: '28px',
        lineHeight: '56px'
    },
    cross: {
        width: '56px',
        height: '56px',
        margin: '0 auto 24px',
        borderRadius: '50%',
        background: '#fdecea',
        color: '#a8071a',
        fontSize: '28px',
        lineHeight: '56px',
        fontWeight: 700
    },
    title: { fontSize: '22px', fontWeight: 700, margin: '0 0 8px 0', color: '#1a1a1a' },
    subtitle: { fontSize: '15px', color: '#666', margin: '0 0 16px 0' },
    helper: { fontSize: '14px', color: '#888' },
    button: {
        display: 'block',
        width: '100%',
        padding: '14px 20px',
        border: 'none',
        borderRadius: '12px',
        background: '#007EA0',
        color: '#fff',
        fontSize: '16px',
        fontWeight: 700,
        cursor: 'pointer',
        marginTop: '8px'
    },
    link: { color: '#007EA0' }
};
